import { HGIntervals } from "./types";

/**
 * EDTF (ISO 8601-2) date qualifiers, applied to one endpoint of a record's
 * time: `~` approximate ("circa") and `?` uncertain ("doubtful"). They are
 * independent -- both set is EDTF's `%` -- and neither affects interval math.
 */
export interface IntervalQualifiers {
  approximate?: boolean;
  uncertain?: boolean;
}

export const APPROXIMATE_HINT =
  "About this date, but not exact — “circa”. Use it when the date is close to right.";
export const UNCERTAIN_HINT =
  "This date is doubtful and may turn out to be wrong.";

/** Qualifiers of a record's (or form value's) start interval. */
export function startQualifiers(
  intervals: Partial<HGIntervals> | null | undefined,
): IntervalQualifiers {
  return {
    approximate: !!intervals?.interval_approximate,
    uncertain: !!intervals?.interval_uncertain,
  };
}

/** Qualifiers of a record's (or form value's) end interval. */
export function endQualifiers(
  intervals: Partial<HGIntervals> | null | undefined,
): IntervalQualifiers {
  return {
    approximate: !!intervals?.end_interval_approximate,
    uncertain: !!intervals?.end_interval_uncertain,
  };
}

export function hasQualifiers(q: IntervalQualifiers): boolean {
  return !!q.approximate || !!q.uncertain;
}

/**
 * Decorates an interval name with its qualifiers: "c. 1750", "1750?" or
 * "c. 1750?". The single place this rule lives, so the record form's preview
 * and the record views can never drift apart.
 */
export function qualifyName(name: string, q: IntervalQualifiers): string {
  if (!name) return name;
  let qualified = name;
  if (q.approximate) qualified = `c. ${qualified}`;
  if (q.uncertain) qualified = `${qualified}?`;
  return qualified;
}

/** Plain-language reading of the qualifiers, for tooltips and title text. */
export function qualifierLabel(q: IntervalQualifiers): string | undefined {
  if (q.approximate && q.uncertain) {
    return "Approximate and uncertain: about this date, and doubtful";
  }
  if (q.approximate) return "Approximate: about this date, not exact";
  if (q.uncertain) return "Uncertain: this date may be wrong";
  return undefined;
}
